import { SECONDS_PER_SUGGESTION } from '../constants';
import {
  FreeSampleOpportunitySnapshotSchema,
  type FreeSampleOpportunity,
  type FreeSampleOpportunitySnapshot,
} from '../schemas/free-sample-opportunity';

export const FREE_SAMPLE_SCOPE_MIN_MESSAGES = 3_000;
export const FREE_SAMPLE_SCOPE_FRESHNESS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Derives one normalized estimate from the completed scan's own persisted
 * scope snapshot and terminal classified population. Apply outcomes are
 * deliberately not an input.
 */
export function computeFreeSampleOpportunity(
  input: FreeSampleOpportunitySnapshot,
  now: number = Date.now(),
): FreeSampleOpportunity | null {
  if (!Number.isFinite(now)) return null;
  const parsed = FreeSampleOpportunitySnapshotSchema.safeParse(input);
  if (!parsed.success) return null;

  const snapshot = parsed.data;
  if (
    snapshot.status !== 'COMPLETED'
    || !snapshot.sample_complete
    || snapshot.completed_with_pro_entitlement
    || snapshot.emails_classified <= 0
    || snapshot.completed_sample_population !== snapshot.emails_classified
    || snapshot.scope_messages_total === null
    || snapshot.scope_messages_total < FREE_SAMPLE_SCOPE_MIN_MESSAGES
    || !snapshot.scope_precision
    || !snapshot.fetched_at
    || !Number.isSafeInteger(snapshot.emails_classified)
    || !Number.isSafeInteger(snapshot.scope_messages_total)
  ) return null;

  const fetchedAt = Date.parse(snapshot.fetched_at);
  if (!Number.isFinite(fetchedAt) || fetchedAt > now || now - fetchedAt >= FREE_SAMPLE_SCOPE_FRESHNESS_MS) return null;

  const additionalEmails = Math.max(0, snapshot.scope_messages_total - snapshot.completed_sample_population);
  const estimatedSeconds = additionalEmails * SECONDS_PER_SUGGESTION;
  if (additionalEmails <= 0 || !Number.isSafeInteger(estimatedSeconds)) return null;

  return {
    additional_emails: additionalEmails,
    estimated_seconds: estimatedSeconds,
  };
}
